import { el, setChildren } from 'redom';
import { debounce } from 'lodash';

import { getPropertyValues } from 'utilities/collection';

import OutsideClickEventRegistration from 'lib/outside-click-event-registration';

import NavigableList from 'generics/components/navigable-list/native';

import MultipleValuesInput from '../multiple-values-input';
import AnchorButton from '../anchor-button';
import OptionsPositioner from '../options-positioner';

class MultipleValuesSelector {

  constructor(options) {

    this.active = false;
    this.outsideEventRegistration = null;
    this.optionsLoaded = false;
    this.field = options.field;
    this.options = options.options;
    this.values = options.values;
    this.placeholder = options.placeholder;
    this.optionsLoader = options.optionsLoader;

    this.onSelect = options.onSelect;
    this.onClose = options.onClose;

    this.anchorButton = null;
    this.input = null;
    this.optionsList = null;

    this.initializeAnchorButton(options);
    this.initializeInput();
    this.initializeOptionsList();

    this.el = el('.multiple-values-selector-main', [this.anchorButton]);

    this.positioner = new OptionsPositioner(this.el, this.input.el, this.optionsList.el.el);

  }

  onmount() {

    this.registerOutsideEventHandler();
    this.positioner.start();

  }

  onunmount() {

    this.unregisterOutsideEventHandler();
    this.positioner.stop();

  }

  showOptions() {

    this.positioner.applyPosition();
    this.positioner.scheduleReposition();

  }

  initializeAnchorButton(options) {

    this.anchorButton = new AnchorButton({
      placeholder: options.placeholder,
      value: getPropertyValues(this.values, 'label').join(', '),
      onClick: this.focus.bind(this)
    });

  }

  initializeInput() {

    const options = {
      placeholder: this.placeholder,
      onSearchTextChange: debounce(this.handleSearchTextChange.bind(this), 250),
      onChange: this.handleValuesChange.bind(this),
      onArrowUpKey: this.handleArrowUpKey.bind(this),
      onArrowDownKey: this.handleArrowDownKey.bind(this),
      onEnterKey: this.handleEnterKey.bind(this),
      values: this.values
    };

    this.input = new MultipleValuesInput(options);

  }

  initializeOptionsList() {

    const options = {
      entries: this.options,
      selectedEntries: this.values,
      onSelect: this.handleValueSelect.bind(this),
      onClose() {},
      scrollAnchorElement: this.input,
      multiselect: true
    };

    this.optionsList = new NavigableList(options);

    if (this.options.length === 0) {
      this.optionsList.showNoResultsFound();
    }

  }

  focus() {

    this.active = true;
    this.anchorButton.open();

    setChildren(this.el, [this.input, this.optionsList]);

    this.el.classList.add('focused');
    this.input.focus();

  }

  setWorking() {

    this.optionsList.setWorking();

  }

  setIdle() {

    this.optionsList.setIdle();

  }

  async handleSearchTextChange(searchText) {

    this.optionsList.setWorking();

    const options = await this.optionsLoader(searchText);

    this.optionsList.setIdle();
    this.optionsList.setEntries(options);

    if (options.length === 0) {
      this.optionsList.showNoResultsFound();
    }

    this.showOptions();

  }

  handleValuesChange(values) {

    this.values = values;
    this.optionsList.setSelectedEntries(values);
    this.onSelect(values);
    this.focus();
    this.input.reset();
    this.showOptions();

  }

  handleArrowUpKey() {

    this.optionsList.highlightPreviousOption();

  }

  handleArrowDownKey() {

    this.optionsList.highlightNextOption();

  }

  handleEnterKey() {

    this.optionsList.selectHighlightedOption();
    this.focus();

  }

  outsideClickHandler() {

    this.active = false;

    this.optionsList.resetHighlighted();

    this.anchorButton.configure({
      placeholder: this.placeholder,
      value: getPropertyValues(this.values, 'label').join(', ')
    });
    this.anchorButton.close();
    setChildren(this.el, [this.anchorButton]);
    this.el.classList.remove('focused');

    this.onClose(this.values);

  }

  registerOutsideEventHandler() {

    this.outsideEventRegistration = new OutsideClickEventRegistration(this.el, this.outsideClickHandler.bind(this));

  }

  unregisterOutsideEventHandler() {

    this.outsideEventRegistration.cancel();

  }

  setOptions(options) {

    this.options = options;
    this.optionsList.setEntries(this.options);

    if (this.options.length === 0) {
      this.optionsList.showNoResultsFound();
    }

  }

  handleValueSelect(values) {

    this.setValues(values);
    this.handleValuesChange(values);

  }

  setValues(values) {

    this.values = values;
    this.input.setValues(this.values);
    this.optionsList.setSelectedEntries(this.values);
    this.focus();

  }

  setValue(values) {

    this.setValues(values);

  }

  clear() {

    this.input.value = '';
    this.optionsList.unsetFilter();

  }

}

export default MultipleValuesSelector;